import { ApiError } from "./ApiErrorResponse";
import { storage } from "./storage-manager";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

interface RequestOptions {
  includeBusinessId?: boolean;
}

export class ApiClient {
  private static refreshTokenCallback: (() => Promise<string | null>) | null =
    null;
  private static isRefreshing: boolean = false;
  private static pendingRequests: Array<{
    resolve: (token: string | null) => void;
  }> = [];

  static setRefreshTokenCallback(callback: () => Promise<string | null>) {
    this.refreshTokenCallback = callback;
  }

  private static async refreshToken(): Promise<string | null> {
    if (this.isRefreshing) {
      return new Promise<string | null>((resolve) => {
        this.pendingRequests.push({ resolve });
      });
    }

    this.isRefreshing = true;

    try {
      if (!this.refreshTokenCallback) {
        return null;
      }
      const newToken = await this.refreshTokenCallback();

      this.pendingRequests.forEach((request) => request.resolve(newToken));
      this.pendingRequests = [];
      return newToken;
    } catch (error) {
      console.error("Error refreshing token:", error);
      this.pendingRequests.forEach((request) => request.resolve(null));
      this.pendingRequests = [];
      return null;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Get current business ID from storage
   */
  private static getCurrentBusinessId(): number | null {
    const currentBusiness = storage.getCurrentBusiness();
    return currentBusiness?.id ?? null;
  }

  /**
   * Build URL with optional business ID prefix
   */
  private static buildUrl(
    endpoint: string,
    includeBusinessId: boolean = false
  ): string {
    if (includeBusinessId) {
      const businessId = this.getCurrentBusinessId();
      if (!businessId) {
        throw new Error(
          "Business ID is required but no current business is selected"
        );
      }
      // Remove leading slash from endpoint if present
      const cleanEndpoint = endpoint.startsWith("/")
        ? endpoint.slice(1)
        : endpoint;
      return `${API_URL}businesses/${businessId}/${cleanEndpoint}`;
    }
    return `${API_URL}${endpoint}`;
  }

  /**
   * Add businessId to headers if available (for backward compatibility)
   */
  private static addBusinessIdHeader(
    headers: Record<string, string>
  ): Record<string, string> {
    const businessId = this.getCurrentBusinessId();
    if (businessId) {
      headers["X-Business-Id"] = String(businessId);
    }
    return headers;
  }

  private static async makeRequest<T>(
    endpoint: string,
    options: RequestInit,
    token?: string,
    includeBusinessId: boolean = false,
    isRetry: boolean = false
  ): Promise<T> {
    let headers: Record<string, string> = {
      ...((options.headers as Record<string, string>) || {}),
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    // Add business ID to headers for backward compatibility
    headers = this.addBusinessIdHeader(headers);

    // Build URL with business ID prefix if needed
    const url = this.buildUrl(endpoint, includeBusinessId);

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401 && !isRetry && this.refreshTokenCallback) {
      console.warn("Token expired, attempting to refresh...");
      const newToken = await this.refreshToken();

      if (newToken) {
        console.log("Token refreshed successfully, retrying request...");
        return this.makeRequest<T>(
          endpoint,
          options,
          newToken,
          includeBusinessId,
          true
        );
      } else {
        console.error("Token refresh failed");
        throw new ApiError("Session expired", 401, response);
      }
    }

    if (!response.ok) {
      let errorMessage = `API error: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } catch {
        errorMessage = response.statusText || errorMessage;
      }
      throw new ApiError(errorMessage, response.status, response);
    }
    return response.json() as Promise<T>;
  }

  static async get<T>(
    endpoint: string,
    token?: string,
    options?: RequestOptions
  ) {
    return this.makeRequest<T>(
      endpoint,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      },
      token,
      options?.includeBusinessId ?? false
    );
  }

  static async post<T>(
    endpoint: string,
    data: unknown,
    token?: string,
    options?: RequestOptions
  ) {
    return this.makeRequest<T>(
      endpoint,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      token,
      options?.includeBusinessId ?? false
    );
  }

  static async postFormData<T>(
    endpoint: string,
    formData: FormData,
    token?: string,
    options?: RequestOptions
  ) {
    return this.makeRequest<T>(
      endpoint,
      {
        method: "POST",
        body: formData,
      },
      token,
      options?.includeBusinessId ?? false
    );
  }

  static async patch<T>(
    endpoint: string,
    data: unknown,
    token?: string,
    options?: RequestOptions
  ) {
    return this.makeRequest<T>(
      endpoint,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      },
      token,
      options?.includeBusinessId ?? false
    );
  }

  static async patchFormData<T>(
    endpoint: string,
    formData: FormData,
    token?: string,
    options?: RequestOptions
  ) {
    return this.makeRequest<T>(
      endpoint,
      {
        method: "PATCH",
        body: formData,
      },
      token,
      options?.includeBusinessId ?? false
    );
  }

  static async delete<T>(
    endpoint: string,
    token?: string,
    options?: RequestOptions
  ) {
    return this.makeRequest<T>(
      endpoint,
      {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      },
      token,
      options?.includeBusinessId ?? false
    );
  }
}
